import {
    Badge,
    BlockStack,
    Button,
    Card,
    Icon,
    InlineStack,
    Page,
    Text,
} from "@shopify/polaris";
import { CheckSmallIcon } from "@shopify/polaris-icons";
import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import { useStore } from "../../app/stores/store";

type PlanId = "standard" | "growth" | "pro" | "enterprise";

type PlanDefinition = {
    id: PlanId;
    name: string;
    price: string;
    cadence?: string;
    trialDays?: number;
    bestFor: string;
    description: string;
    highlights: string[];
    recommended?: boolean;
};

type PlanComparisonValue = boolean | string;

type PlanComparisonSection = {
    title: string;
    rows: Array<{
        label: string;
        values: Record<PlanId, PlanComparisonValue>;
    }>;
};

const plans: PlanDefinition[] = [
    {
        id: "standard",
        name: "Standard",
        price: "$49",
        cadence: "/month",
        trialDays: 14,
        bestFor: "Straightforward Shopify catalogs",
        description: "A complete Shopify-native fitment experience with the tools merchants and shoppers expect.",
        highlights: [
            "Configurable fitment finder with up to 8 selector fields",
            "Complete storefront fitment experience",
            "Basic search activity reports",
        ],
    },
    {
        id: "growth",
        name: "Growth",
        price: "$129",
        cadence: "/month",
        trialDays: 14,
        bestFor: "Larger and more sophisticated catalogs",
        description: "Database-backed fitment and flexible search that grow beyond Shopify-native constraints.",
        highlights: [
            "Database-backed, app-rendered results beyond Shopify's 250-tag-per-product limit",
            "Advanced selector layouts and storefront result controls",
            "Advanced search demand and fitment coverage reports",
        ],
        recommended: true,
    },
    {
        id: "pro",
        name: "Pro",
        price: "$249",
        cadence: "/month",
        trialDays: 14,
        bestFor: "Data automation, ACES, and merchandising",
        description: "Advanced catalog operations, safer data workflows, guided merchandising, and deeper insights.",
        highlights: [
            "ACES XML and advanced import workflows",
            "Product-tag import with preview, review, and undo",
            "Product Guide builder",
        ],
    },
    {
        id: "enterprise",
        name: "Enterprise / Custom",
        price: "Custom",
        bestFor: "Custom scale, data sources, and integrations",
        description: "Flexible infrastructure and hands-on implementation support for complex fitment operations.",
        highlights: [
            "Multi-catalog beta and high-volume limits",
            "Custom data feeds, private integrations, and sync cadence",
            "Dedicated onboarding and support/SLA options",
        ],
    },
];

const comparisonSections: PlanComparisonSection[] = [
    {
        title: "Fitment and catalog",
        rows: [
            {
                label: "Configurable selector fields",
                values: { standard: "Up to 8", growth: "Up to 8", pro: "Up to 8", enterprise: "Up to 8" },
            },
            {
                label: "Fitment catalogs",
                values: { standard: "1", growth: "1", pro: "1", enterprise: "Multi-catalog beta" },
            },
            {
                label: "Product and variant fitment mapping",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Universal products",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Turn14-compatible fitment through CSV/XLSX imports",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
        ],
    },
    {
        title: "Storefront experience",
        rows: [
            {
                label: "Theme app extension and storefront styling",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Persistent vehicle selection and My Garage",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Product-page fitment confirmation",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Product compatibility table",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Result architecture",
                values: {
                    standard: "Shopify tag-backed",
                    growth: "Database-backed",
                    pro: "Database-backed",
                    enterprise: "Database-backed + custom options",
                },
            },
            {
                label: "Results beyond Shopify's 250-tag-per-product limit",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
            {
                label: "App-rendered result pages",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Fitment Notes",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Results grouped and filtered by collection or product type",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Advanced selector layouts and storefront result controls",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
        ],
    },
    {
        title: "Imports and merchandising",
        rows: [
            {
                label: "CSV/XLSX fitment import",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "CSV fitment export",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "ACES XML import",
                values: { standard: false, growth: false, pro: true, enterprise: true },
            },
            {
                label: "Product-tag import with preview, review, and undo",
                values: { standard: false, growth: false, pro: true, enterprise: true },
            },
            {
                label: "Collection-mapping imports and advanced replacement workflows",
                values: { standard: false, growth: false, pro: true, enterprise: true },
            },
            {
                label: "Product Guide builder",
                values: { standard: false, growth: false, pro: true, enterprise: true },
            },
        ],
    },
    {
        title: "Reporting",
        rows: [
            {
                label: "Daily search volume",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Top vehicle searches",
                values: { standard: true, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Coverage opportunities",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Search demand trends",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
            {
                label: "Fitment coverage health",
                values: { standard: false, growth: true, pro: true, enterprise: true },
            },
        ],
    },
    {
        title: "Support and scale",
        rows: [
            {
                label: "Support",
                values: {
                    standard: "Standard",
                    growth: "Priority",
                    pro: "Priority + setup/import guidance",
                    enterprise: "Custom/SLA options",
                },
            },
            {
                label: "Large migrations and high-volume limits",
                values: { standard: false, growth: false, pro: false, enterprise: true },
            },
            {
                label: "Custom data feeds, integrations, and sync cadence",
                values: { standard: false, growth: false, pro: false, enterprise: true },
            },
            {
                label: "Dedicated implementation and onboarding",
                values: { standard: false, growth: false, pro: false, enterprise: true },
            },
        ],
    },
];

const renderComparisonValue = (value: PlanComparisonValue) => {
    if (value === true) {
        return (
            <span className="pf-plan-comparison__included" aria-label="Included">
                <Icon source={CheckSmallIcon} />
            </span>
        );
    }

    if (value === false) {
        return <span className="pf-plan-comparison__unavailable" aria-label="Not included">&mdash;</span>;
    }

    return <Text as="span" variant="bodySm">{value}</Text>;
};

const resolveCurrentPlan = (subscriptionName?: string): PlanId => {
    const normalizedName = subscriptionName?.trim().toLowerCase() ?? "";

    if (/\b(enterprise|custom)\b/.test(normalizedName)) return "enterprise";
    if (/\b(pro|professional)\b/.test(normalizedName)) return "pro";
    if (/\b(growth|plus)\b/.test(normalizedName)) return "growth";

    // Existing subscriptions can use legacy names such as Core, Basic, or Standard.
    return "standard";
};

type PlanPageProps = {
    embedded?: boolean;
};

const PlanPage = ({ embedded = false }: PlanPageProps) => {
    const { userStore } = useStore();
    const [searchParams] = useSearchParams();
    const showPricing = searchParams.get("showPricing") === "true";
    const currentPlanId = resolveCurrentPlan(userStore.user?.subscription?.name);
    const currentPlan = plans.find((plan) => plan.id === currentPlanId) ?? plans[0];

    const content = (
        <div className="pf-plan-page">
                <BlockStack gap="600">
                    <Card>
                        <div className="pf-plan-current">
                            <div className="pf-plan-current__content">
                                <BlockStack gap="200">
                                    <InlineStack gap="200" blockAlign="center">
                                        <Text as="p" variant="headingSm">Your current plan</Text>
                                        <Badge tone="success">Active</Badge>
                                    </InlineStack>
                                    <Text as="h2" variant="heading2xl">{currentPlan.name}</Text>
                                    <Text as="p" tone="subdued">
                                        Your current access and billing remain unchanged. Plan changes are not available in the app yet.
                                    </Text>
                                </BlockStack>
                            </div>
                            {showPricing && (
                                <div className="pf-plan-current__preview">
                                    <Badge tone="info">Pricing preview</Badge>
                                </div>
                            )}
                        </div>
                    </Card>

                    <section aria-labelledby="plan-comparison-heading">
                        <BlockStack gap="400">
                            <div className="pf-plan-section-heading">
                                <Text id="plan-comparison-heading" as="h2" variant="headingLg">
                                    Plans at a glance
                                </Text>
                                <Text as="p" tone="subdued">
                                    Start with the plan that matches your catalog today. You can review every feature below.
                                </Text>
                            </div>

                            <div className="pf-plan-grid">
                                {plans.map((plan) => {
                                    const isCurrent = plan.id === currentPlanId;

                                    return (
                                        <article
                                            className={`pf-plan-card${isCurrent ? " pf-plan-card--current" : ""}`}
                                            key={plan.id}
                                        >
                                            <Card>
                                                <BlockStack gap="500">
                                                    <div className="pf-plan-card__header">
                                                        <BlockStack gap="200">
                                                            <InlineStack gap="200" blockAlign="center" wrap>
                                                                <Text as="h3" variant="headingLg">{plan.name}</Text>
                                                                {isCurrent && <Badge tone="success">Current plan</Badge>}
                                                                {plan.recommended && <Badge tone="info">Recommended</Badge>}
                                                            </InlineStack>

                                                            <Text as="p" variant="bodySm">
                                                                <span className="pf-plan-card__best-for">Best for:</span> {plan.bestFor}
                                                            </Text>

                                                            {showPricing && (
                                                                <BlockStack gap="100">
                                                                    <div className="pf-plan-card__price" aria-label={`${plan.name} price: ${plan.price}${plan.cadence ?? ""}`}>
                                                                        <span>{plan.price}</span>
                                                                        {plan.cadence && <small>{plan.cadence}</small>}
                                                                    </div>
                                                                    {plan.trialDays && (
                                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                                            {plan.trialDays}-day free trial
                                                                        </Text>
                                                                    )}
                                                                </BlockStack>
                                                            )}

                                                            <Text as="p" tone="subdued">{plan.description}</Text>
                                                        </BlockStack>
                                                    </div>

                                                    <div className="pf-plan-card__action">
                                                        <Button>
                                                            {plan.id === "enterprise" ? "Contact team" : "Select plan"}
                                                        </Button>
                                                    </div>

                                                    <div className="pf-plan-card__divider" />

                                                    <ul className="pf-plan-feature-list">
                                                        {plan.highlights.map((highlight) => (
                                                            <li key={highlight}>
                                                                <span className="pf-plan-feature-list__icon" aria-hidden="true">
                                                                    <Icon source={CheckSmallIcon} />
                                                                </span>
                                                                <Text as="span" variant="bodyMd">{highlight}</Text>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </BlockStack>
                                            </Card>
                                        </article>
                                    );
                                })}
                            </div>
                        </BlockStack>
                    </section>

                    <section aria-labelledby="plan-feature-comparison-heading">
                        <BlockStack gap="400">
                            <div className="pf-plan-section-heading">
                                <Text id="plan-feature-comparison-heading" as="h2" variant="headingLg">
                                    Compare every feature
                                </Text>
                                <Text as="p" tone="subdued">
                                    See exactly what is included and where each plan adds more capability.
                                </Text>
                            </div>

                            <div
                                className="pf-plan-comparison"
                                role="region"
                                aria-label="Detailed plan comparison"
                                tabIndex={0}
                            >
                                <table>
                                    <caption className="pf-plan-visually-hidden">
                                        Detailed comparison of Standard, Growth, Pro, and Enterprise plans
                                    </caption>
                                    <thead>
                                        <tr>
                                            <th className="pf-plan-comparison__feature-heading" scope="col">
                                                Feature
                                            </th>
                                            {plans.map((plan) => {
                                                const isCurrent = plan.id === currentPlanId;

                                                return (
                                                    <th
                                                        className={isCurrent ? "pf-plan-comparison__current" : undefined}
                                                        key={plan.id}
                                                        scope="col"
                                                    >
                                                        <div className="pf-plan-comparison__plan-heading">
                                                            <span>{plan.name}</span>
                                                            {isCurrent && <Badge tone="success">Current</Badge>}
                                                            {plan.recommended && <Badge tone="info">Recommended</Badge>}
                                                            {showPricing && (
                                                                <div className="pf-plan-comparison__price">
                                                                    {plan.price}{plan.cadence ?? ""}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    {comparisonSections.map((section) => (
                                        <tbody key={section.title}>
                                            <tr className="pf-plan-comparison__section-row">
                                                <th colSpan={plans.length + 1} scope="colgroup">
                                                    {section.title}
                                                </th>
                                            </tr>
                                            {section.rows.map((row) => (
                                                <tr key={row.label}>
                                                    <th scope="row">{row.label}</th>
                                                    {plans.map((plan) => (
                                                        <td
                                                            className={plan.id === currentPlanId ? "pf-plan-comparison__current" : undefined}
                                                            key={plan.id}
                                                        >
                                                            {renderComparisonValue(row.values[plan.id])}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    ))}
                                </table>
                            </div>
                            <Text as="p" variant="bodySm" tone="subdued">
                                Plan selection is currently a preview. Your existing access and billing will not change.
                            </Text>
                        </BlockStack>
                    </section>
                </BlockStack>
        </div>
    );

    if (embedded) {
        return content;
    }

    return (
        <Page
            title="Plan"
            subtitle="See what is included in your plan and compare available options."
            fullWidth
        >
            {content}
        </Page>
    );
};

export default observer(PlanPage);
